import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Spinner } from "react-bootstrap";
import Select from "react-select";
import { useParams } from "react-router-dom";


const PayrollView = () => {
    const { id } = useParams();
    const [payrollData, setPayrollData] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState("");

    useEffect(() => {
        fetchDepartments();
    }, [])

    const fetchDepartments = async () => {
        try {
            const response = await api.get("/departments");
            setDepartments(response.data.data);
        } catch (error) {
            console.error("Error fetching departments: ", error);
            toast.error("Failed to fetch departments");
        }
    };

    const fetchPayrollData = async (departmentId = "") => {
        setIsLoading(true);
        try {
            const response = await api.get(`/payrolls/${id}/details?department_id=${departmentId}`);
            setPayrollData(response.data);
        } catch (error) {
            if (error.data && error.data.errors) {
                error.data.errors.forEach(err => {
                    toast.error(err);
                });
            } else {
                toast.error("Failed to fetch payroll data");
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPayrollData();
    }, [id]);

    const handlePrintPage = () => {
        window.open(`/user/payrolls/print?id=${id}&departmentId=${selectedDepartment}`, "_blank");
    };

    const handlePayslip = () => {
        window.open(`/user/payrolls/payslip?id=${id}&departmentId=${selectedDepartment}`, "_blank");
    };

    // Calculate totals for each column
    const calculateTotals = () => {
        const totals = {
            actual_days: 0,
            extra: 0,
            calc_days: 0,
            pay_rate: 0,
            bonus: 0,
            grand_total: 0,
        };

        payrollData?.payroll_entries?.forEach(entry => {
            totals.actual_days += Number(entry.actual_days) || 0;
            totals.extra += Number(entry.extra) || 0;
            totals.calc_days += Number(entry.calc_days) || 0;
            totals.pay_rate += Number(entry.pay_rate) || 0;
            totals.bonus += Number(entry.bonus) || 0;
            totals.grand_total += Number(entry.grand_total) || 0;
        });

        totals.actual_days = Math.round(totals.actual_days * 10) / 10;
        totals.extra = Math.round(totals.extra * 10) / 10;
        totals.calc_days = Math.round(totals.calc_days * 10) / 10;
        totals.pay_rate = Math.round(totals.pay_rate * 10) / 10;
        totals.bonus = Math.round(totals.bonus * 10) / 10;
        totals.grand_total = Math.round(totals.grand_total * 10) / 10;

        return totals;
    };

    const totals = calculateTotals();

    const formatNumber = (value) => {
        const roundedValue = Math.round(value * 10) / 10;
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundedValue);
    };

    const handleDepartmentChange = (event) => {
        setSelectedDepartment(event.target.value);
    };

    const handleSearch = () => {
        fetchPayrollData(selectedDepartment);
    };

    const handleClear = () => {
        setSelectedDepartment("");
        fetchPayrollData();
    };

    return (
        <>
            <main id="main" className="main">
                <div className="pagetitle mb-4">
                    <h1>Payroll View</h1>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="card-title mb-0">Payroll - {payrollData?.title}</h5>
                                        <div>
                                            <button className="btn btn-primary me-2" onClick={handlePayslip} title="Print Pay Slips">
                                                <span className="d-none d-sm-inline-block">Print Pay Slips</span> <i className="bi bi-printer"></i>
                                            </button>
                                            <button className="btn btn-primary" onClick={handlePrintPage} title="Print Payroll">
                                                <span className="d-none d-sm-inline-block">Payroll</span> <i className="bi bi-printer"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row g-2 mb-4">
                                        <div className="col-12 col-sm-5 col-md-3">
                                            <Select
                                                className="basic-single"
                                                classNamePrefix="select"
                                                value={selectedDepartment
                                                    ? departments.find(dept => dept.department_id === selectedDepartment)
                                                        ? {
                                                            value: selectedDepartment,
                                                            label: departments.find(dept => dept.department_id === selectedDepartment)?.department_name
                                                        }
                                                        : null
                                                    : null
                                                }
                                                onChange={(selectedOption) => setSelectedDepartment(selectedOption ? selectedOption.value : '')}
                                                options={departments.map(dept => ({ value: dept.department_id, label: dept.department_name }))}
                                                placeholder="All Departments"
                                                isSearchable
                                                isClearable
                                                noOptionsMessage={() => "No options"}
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderRadius: "6px",
                                                    }),
                                                    placeholder: (provided) => ({
                                                        ...provided,
                                                        whiteSpace: "nowrap",
                                                    })
                                                }}
                                            />
                                        </div>
                                        <div className="col-12 col-sm-7 col-md-9">
                                            <button className="btn btn-primary"
                                                onClick={handleSearch}
                                            >
                                                Search
                                            </button>
                                            <button className="btn btn-secondary ms-2" onClick={handleClear}>
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                    {isLoading ? (
                                        <div className="text-center mt-3">
                                            <Spinner animation="border" variant="primary" />
                                            <span className="ms-2">Loading...</span>
                                        </div>
                                    ) : (
                                        <>
                                            {payrollData?.payroll_entries?.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th width="180">Name</th>
                                                                <th width="130">Actual Days</th>
                                                                <th width="130">Extra</th>
                                                                <th width="130">Total Days</th>
                                                                <th className="text-center" width="130">Pay Rate</th>
                                                                <th className="text-center" width="130">Bonus</th>
                                                                <th width="130" className="text-center">Grand Total</th>
                                                                <th width="130" className="text-end">Signature</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {payrollData?.payroll_entries.map((item, index) => (
                                                                <tr key={item.employee_id}>
                                                                    <td>{item?.employee?.first_name} {item?.employee?.last_name}
                                                                        <p className="m-0 small">({item?.employee.role?.role_name})</p>
                                                                    </td>
                                                                    <td>{item.actual_days}</td>
                                                                    <td>{item.extra}</td>
                                                                    <td>{item.calc_days}</td>
                                                                    <td className="text-end">{formatNumber(item.pay_rate)}</td>
                                                                    <td className="text-end">{formatNumber(item.bonus)}</td>
                                                                    <td className="text-end">{formatNumber(item.grand_total)}</td>
                                                                    <td className="text-end" style={{ verticalAlign: 'bottom' }}>
                                                                        <div className="ml-auto w-75 border-dark border-bottom"></div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            {/* Add the totals row */}
                                                            <tr>
                                                                <td className="text-end"><strong>Total:</strong></td>
                                                                <td><strong>{totals.actual_days}</strong></td>
                                                                <td><strong>{totals.extra}</strong></td>
                                                                <td><strong>{totals.calc_days}</strong></td>
                                                                <td className="text-end"><strong>{formatNumber(totals.pay_rate)}</strong></td>
                                                                <td className="text-end"><strong>{formatNumber(totals.bonus)}</strong></td>
                                                                <td className="text-end"><strong>{formatNumber(totals.grand_total)}</strong></td>
                                                                <td className="text-end"></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p>No payroll data available.</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
};

export default PayrollView;